import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { AlertTriangle, DollarSign, BarChart3, Users } from 'lucide-react';

const AdvertisingDisclosurePage = () => {
  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h1 className="text-3xl md:text-4xl font-black text-[#d9fb06] uppercase mb-8">
            Advertising Disclosure
          </h1>
          
          <div className="prose prose-invert max-w-none space-y-8 text-[#888680]">
            <p className="text-sm">Last updated: February 2025</p>
            
            {/* FTC Disclosure Box */}
            <div className="bg-[#302f2c] border-2 border-[#d9fb06]/50 rounded-xl p-6 my-8">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-[#d9fb06] flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-lg font-bold text-[#d9fb06] mb-2">FTC Disclosure</h2>
                  <p className="text-[#dfddd6]">
                    In accordance with the FTC's guidelines concerning endorsements and testimonials, 
                    please be aware that Social Dispute Co. may receive compensation for services 
                    rendered. This disclosure is provided to ensure full transparency with our visitors 
                    and potential clients.
                  </p>
                </div>
              </div>
            </div>
            
            <section>
              <h2 className="text-xl font-bold text-[#dfddd6] mt-8 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#d9fb06]" />
                Material Connection Disclosure
              </h2>
              <p>
                Social Dispute Co. operates as a paid service provider. When you engage our services, 
                you are entering into a business transaction. Our fees cover:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Professional case review and assessment</li>
                <li>Document preparation and compilation</li>
                <li>Professionally reviewed correspondence</li>
                <li>Preparation for official platform appeal channels</li>
                <li>Case monitoring and follow-up</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-bold text-[#dfddd6] mt-8 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#d9fb06]" />
                Results Disclaimer
              </h2>
              <div className="bg-[#302f2c] p-6 rounded-xl border border-[#3f4816]/50">
                <p className="font-semibold text-[#dfddd6] mb-3">
                  IMPORTANT: Results Are Not Guaranteed
                </p>
                <p>
                  Past performance does not guarantee future results. Success rates mentioned on our 
                  website are based on historical data and individual case outcomes vary significantly 
                  based on:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-3">
                  <li>The specific platform's policies and enforcement practices</li>
                  <li>Your account's history and standing</li>
                  <li>The nature and severity of alleged violations</li>
                  <li>Quality and completeness of evidence provided</li>
                  <li>Platform discretion and timing</li>
                </ul>
                <p className="mt-4 text-sm italic">
                  We cannot and do not guarantee account reinstatement. Platform decisions are made 
                  solely by the respective platforms and are outside our control.
                </p>
              </div>
            </section>
            
            <section>
              <h2 className="text-xl font-bold text-[#dfddd6] mt-8 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#d9fb06]" />
                Testimonials & Reviews
              </h2>
              <p>
                Testimonials displayed on our website represent individual client experiences. These 
                testimonials:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Reflect the experiences of specific individuals</li>
                <li>Do not guarantee similar outcomes for other clients</li>
                <li>May have been edited for clarity or brevity</li>
                <li>May represent atypical results</li>
              </ul>
              <p className="mt-4">
                We encourage you to make decisions based on your own research and consultation with 
                our team, rather than solely on testimonials.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-bold text-[#dfddd6] mt-8 mb-4">
                Affiliate & Referral Compensation
              </h2>
              <p>
                Social Dispute Co. operates a referral program where individuals may receive 
                compensation for successful referrals. If you were referred to us by an affiliate 
                or referral partner, that individual may receive compensation upon successful 
                completion of services.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-bold text-[#dfddd6] mt-8 mb-4">
                Advertising Platforms
              </h2>
              <p>
                Social Dispute Co. may advertise on various platforms including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Google (Search, Display, YouTube)</li>
                <li>Meta (Facebook, Instagram)</li>
                <li>Microsoft (Bing)</li>
                <li>Reddit</li>
                <li>Quora</li>
                <li>Other digital advertising networks</li>
              </ul>
              <p className="mt-4">
                All advertising complies with respective platform policies and applicable advertising 
                regulations. Advertisements are clearly identified as such.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-bold text-[#dfddd6] mt-8 mb-4">
                Legal Services Notice
              </h2>
              <div className="bg-[#302f2c] p-6 rounded-xl border border-[#3f4816]/50">
                <p className="font-semibold text-[#dfddd6] mb-3">
                  Social Dispute Co. is NOT a law firm.
                </p>
                <p>
                  We provide dispute documentation preparation and submission services. While licensed 
                  Social Dispute Co. is not a law firm and does not provide legal advice. 
                  Engagement with Social Dispute Co. does not create any professional-client relationship.
                </p>
              </div>
            </section>
            
            <section>
              <h2 className="text-xl font-bold text-[#dfddd6] mt-8 mb-4">
                Contact for Advertising Inquiries
              </h2>
              <p>
                For questions about our advertising practices or this disclosure, contact us at:
              </p>
              <p className="text-[#dfddd6] mt-4">
                Email: intake@socialdisputeco.com<br />
                Phone: 808-301-2134
              </p>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AdvertisingDisclosurePage;
